import React from 'react'
import {formatCents, formatFrenchDate, groupPromotionLines, situationDe} from '@azoya-spa/contracts'
import {ServicePrice} from '../Services/ServicePrice'

// Le tableau d'une promotion. Sa forme suit la situation tarifaire des
// prestations retenues — et c'est précisément pourquoi l'administration impose
// qu'elles la partagent toutes : deux situations dans le même tableau
// demanderaient deux jeux de colonnes incompatibles.
//
// Cas « Avec pass / sans pass » : prix barré à côté du prix remisé. C'est la
// seule situation qu'a connue cette page jusqu'ici.
//
// Toute autre situation (cure, forfait remisé, séance partagée, prix unique)
// tombe dans la forme générique, qui délègue le rendu du tarif à
// `ServicePrice` — le même composant que la page Services, donc les mêmes
// formulations partout. Le tarif y tient parfois sur trois lignes (« 30 € la
// séance », « 80 € les 3 séances », « 10 € d'économie ») : c'est la raison
// pour laquelle ces cellules ne se réduisent pas à une paire de montants.
//
// Les deux formes partagent une seule structure : un en-tête de colonnes, puis
// une ligne par prestation dont chaque cellule porte son propre libellé de
// colonne. Sous 800 px, l'en-tête disparaît et les libellés se montrent : la
// ligne devient une carte. C'est la technique de `Prose` (`tableLayout="cards"`)
// appliquée à la main, `Prose` ne produisant que le balisage qu'on lui donne.
// Un seul balisage, donc : rien n'est rendu deux fois pour être masqué une fois.
//
// Cas « forfait seulement » (`appliesTo: 'forfait'`) : seul le forfait baisse.
// Une phrase dit la promotion avant le tableau ; le forfait vient en premier,
// avec une seule économie — celle de la promotion, pas celle du forfait contre
// les séances à l'unité ; la séance est rangée à part, au prix habituel, jamais
// barrée. Les lignes sont regroupées par groupe du catalogue, et chaque carte
// porte le sien : deux « Aisselles » (Femmes, Hommes) doivent se distinguer.

function Paire({avant, apres}) {
    return (
        <span className="oa-promo-paire">
            <span className="oa-promo-avant">{formatCents(avant)}</span>
            <span className="oa-promo-apres">{formatCents(apres)}</span>
        </span>
    )
}

/** Montant non proposé : un tiret cadratin plutôt qu'une cellule vide. */
function Absent() {
    return <span className="oa-promo-absent">—</span>
}

/**
 * Les cellules d'une ligne, dans l'ordre des colonnes. Chacune porte le
 * libellé de sa colonne : c'est lui qui rend la carte lisible en dessous de
 * 800 px, et il reste annoncé aux lecteurs d'écran au-dessus.
 */
function cellulesDe(ligne, parPass) {
    if (parPass) {
        const {original, discounted} = ligne
        return [
            {
                libelle: 'Avec pass',
                valeur: original.avecPass !== null
                    ? <Paire avant={original.avecPass} apres={discounted.avecPass}/>
                    : <Absent/>,
                economie: original.avecPass !== null ? ligne.savingLabel : null,
            },
            {
                libelle: 'Sans pass',
                valeur: original.sansPass !== null
                    ? <Paire avant={original.sansPass} apres={discounted.sansPass}/>
                    : <Absent/>,
                economie: null,
            },
        ]
    }

    return [
        {
            libelle: 'Tarif habituel',
            valeur: <span className="oa-promo-barre"><ServicePrice amounts={ligne.original}/></span>,
            economie: null,
        },
        {
            libelle: 'Pendant la promotion',
            valeur: <ServicePrice amounts={ligne.discounted}/>,
            economie: ligne.savingLabel,
        },
    ]
}

/** « 8 » quand tous les forfaits ont le même nombre de séances, `null` sinon. */
function nombreCommun(lignes) {
    const nombres = new Set(lignes.map((ligne) => ligne.original.nombreSeances))
    return nombres.size === 1 ? [...nombres][0] : null
}

function cellulesForfaitSeul(ligne) {
    const {original, discounted} = ligne
    return [
        {
            libelle: `Forfait de ${original.nombreSeances} séances, pendant la promotion`,
            valeur: (
                <>
                    <span className="oa-promo-forfait">
                        <span className="oa-promo-apres">{formatCents(discounted.prixForfait)}</span>
                        {' '}les {original.nombreSeances} séances
                    </span>
                    {/* « au lieu de » dit l'avant en toutes lettres : le barré
                        seul n'est pas annoncé par les lecteurs d'écran. */}
                    <span className="oa-promo-au-lieu">
                        au lieu de <s>{formatCents(original.prixForfait)}</s>
                    </span>
                </>
            ),
            economie: ligne.savingLabel,
        },
        {
            libelle: 'Séance à l’unité, prix habituel',
            valeur: <span className="oa-promo-seance">{formatCents(original.prixSeance)} la séance</span>,
            economie: null,
        },
    ]
}

function Cellules({cellules}) {
    return cellules.map((cellule) => (
        <div className="oa-promo-cell" key={cellule.libelle}>
            <span className="oa-promo-cell-label">{cellule.libelle}</span>
            <span className="oa-promo-cell-value">
                {cellule.valeur}
                {cellule.economie && (
                    <span className="oa-promo-economie">{cellule.economie}</span>
                )}
            </span>
        </div>
    ))
}

function TableauForfaitSeul({lignes, discountPercent, fin}) {
    const nombre = nombreCommun(lignes)
    const forfaits = nombre ? `les forfaits de ${nombre} séances` : 'les forfaits'
    return (
        <>
            <div className="oa-promo-annonce">
                <p className="oa-promo-annonce-titre">
                    −{discountPercent} % sur {forfaits}, jusqu’au {formatFrenchDate(fin, true)}
                </p>
                <p className="oa-promo-annonce-suite">La séance à l’unité reste au prix habituel.</p>
            </div>
            <div className="oa-promo-table oa-promo-table--forfait">
                <div className="oa-promo-row oa-promo-row--head" aria-hidden="true">
                    <div>Prestation</div>
                    <div className="oa-promo-cell">
                        {nombre ? `Forfait de ${nombre} séances` : 'Forfait'}
                        <span className="oa-promo-head-precision">pendant la promotion</span>
                    </div>
                    <div className="oa-promo-cell">
                        Séance à l’unité
                        <span className="oa-promo-head-precision">prix habituel, hors promotion</span>
                    </div>
                </div>

                {groupPromotionLines(lignes).map(({group, lines}) => (
                    <React.Fragment key={group ?? ''}>
                        {group && <h3 className="oa-promo-groupe">{group}</h3>}
                        {lines.map((ligne) => (
                            <div className="oa-promo-row" key={ligne.serviceId}>
                                <div className="oa-promo-name">
                                    {/* Le groupe sur chaque carte : à l'écran sous
                                        800 px, aux lecteurs d'écran partout. */}
                                    {group && <span className="oa-promo-name-groupe">{group}</span>}
                                    {ligne.name}
                                </div>
                                <Cellules cellules={cellulesForfaitSeul(ligne)}/>
                            </div>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </>
    )
}

export function PromotionTable({lignes, promotion}) {
    if (lignes.length === 0) return null

    if (promotion?.appliesTo === 'forfait' && situationDe(lignes[0].original) === 'forfait') {
        return (
            <TableauForfaitSeul
                lignes={lignes}
                discountPercent={promotion.discountPercent}
                fin={promotion.season.end}
            />
        )
    }

    // Les prestations partageant toutes la même situation (garanti par l'API),
    // la première suffit à choisir les colonnes.
    const parPass = situationDe(lignes[0].original) === 'pass'
    const colonnes = parPass ? ['Avec pass', 'Sans pass'] : ['Tarif habituel', 'Pendant la promotion']

    return (
        <div className="oa-promo-table">
            {/* Masqué aux lecteurs d'écran : chaque cellule porte déjà son
                libellé de colonne, et l'entendre deux fois n'apprend rien. */}
            <div className="oa-promo-row oa-promo-row--head" aria-hidden="true">
                <div>Prestation</div>
                {colonnes.map((colonne) => (
                    <div className="oa-promo-cell" key={colonne}>{colonne}</div>
                ))}
            </div>

            {lignes.map((ligne) => (
                <div className="oa-promo-row" key={ligne.serviceId}>
                    <div className="oa-promo-name">{ligne.name}</div>
                    <Cellules cellules={cellulesDe(ligne, parPass)}/>
                </div>
            ))}
        </div>
    )
}
